// Wave 14+ / Rapha Fit — derive apparel measurements from the body
// visual hull.
//
// Inputs: the occupancy grid from buildVisualHullBody (Track 2 hull
// extension exposes it). Outputs: a circumference-per-Y series and
// named anatomical measurements (chest / waist / hip / thigh / etc.).
//
// Circumference is computed from the boundary perimeter of each Y
// slice: count voxels that are occupied and have at least one empty
// 4-neighbour in X/Z, then multiply by the voxel step to get metres
// → cm. Coarse but sufficient at the 64×96×40 body grid resolution.
//
// Named anatomical heights are derived from the rider's measured
// height + canonical ratios; the actual measurement at each height is
// the nearest local maximum / minimum in the circumference series so
// we land on real anatomy, not a fixed ratio that mis-targets riders
// with different proportions.

export interface CircumferencePoint {
  y_cm: number;
  circumference_cm: number;
}

export interface FitMeasurements {
  height_cm: number | null;
  chest_cm: number | null;
  waist_cm: number | null;
  hip_cm: number | null;
  thigh_cm: number | null;
  shoulder_width_cm: number | null;
  torso_length_cm: number | null;
  series: CircumferencePoint[];
}

const STEP_SLICES = 2; // sample every other Y slice

/** Walk the occupancy grid Y by Y and compute the perimeter of the
 *  occupied region at each slice, expressed in cm. The perimeter is
 *  approximated as `boundary_voxels × step` where `step` is the average
 *  of the X and Z voxel sizes — close enough at this resolution. */
export function extractCircumferences(
  occupancy: Uint8Array,
  dims: { NX: number; NY: number; NZ: number },
  voxelSize: { x: number; y: number; z: number },
  worldOrigin: { x: number; y: number; z: number },
): CircumferencePoint[] {
  const { NX, NY, NZ } = dims;
  const stepXcm = voxelSize.x * 100;
  const stepYcm = voxelSize.y * 100;
  const stepZcm = voxelSize.z * 100;
  const perimStep = (stepXcm + stepZcm) * 0.5;
  const series: CircumferencePoint[] = [];

  const occ = (x: number, y: number, z: number): number => {
    if (x < 0 || x >= NX || y < 0 || y >= NY || z < 0 || z >= NZ) return 0;
    return occupancy[z * NX * NY + y * NX + x];
  };

  // Y=0 sits at the bottom of the grid → feet. y_cm reports height from
  // the feet (which is what anatomical ratios target).
  for (let vy = 0; vy < NY; vy += STEP_SLICES) {
    let boundary = 0;
    for (let vz = 0; vz < NZ; vz++) {
      for (let vx = 0; vx < NX; vx++) {
        if (!occ(vx, vy, vz)) continue;
        if (
          !occ(vx + 1, vy, vz) ||
          !occ(vx - 1, vy, vz) ||
          !occ(vx, vy, vz + 1) ||
          !occ(vx, vy, vz - 1)
        ) {
          boundary++;
        }
      }
    }
    if (boundary > 0) {
      series.push({
        y_cm: vy * stepYcm,
        circumference_cm: boundary * perimStep,
      });
    }
  }
  return series;
}

/** Match anatomical features (chest peak, waist trough, hip peak,
 *  thigh peak) to the circumference series. Returns null for any
 *  feature whose anatomical region doesn't show a clear local extremum.
 *  Heights are in cm from the feet. */
export function findAnatomicalMeasurements(
  series: CircumferencePoint[],
  riderHeightCm: number | null,
): Pick<FitMeasurements, 'chest_cm' | 'waist_cm' | 'hip_cm' | 'thigh_cm'> {
  if (!series.length || !riderHeightCm) {
    return { chest_cm: null, waist_cm: null, hip_cm: null, thigh_cm: null };
  }

  // Search windows as a fraction of height. Each window is centered
  // on the canonical ratio and ±5 cm wide.
  const window = (centerRatio: number) => {
    const center = riderHeightCm * centerRatio;
    return { lo: center - 5, hi: center + 5 };
  };

  // Pick the local extremum (max for chest/hip/thigh, min for waist)
  // within the window.
  const pick = (lo: number, hi: number, mode: 'max' | 'min'): number | null => {
    let bestY = -1;
    let bestVal = mode === 'max' ? -Infinity : Infinity;
    for (const p of series) {
      if (p.y_cm < lo || p.y_cm > hi) continue;
      if ((mode === 'max' && p.circumference_cm > bestVal) ||
          (mode === 'min' && p.circumference_cm < bestVal)) {
        bestVal = p.circumference_cm;
        bestY = p.y_cm;
      }
    }
    if (bestY < 0 || !Number.isFinite(bestVal)) return null;
    return bestVal;
  };

  const chestW = window(0.72);
  const waistW = window(0.62);
  const hipW = window(0.53);
  const thighW = window(0.43);
  return {
    chest_cm: pick(chestW.lo, chestW.hi, 'max'),
    waist_cm: pick(waistW.lo, waistW.hi, 'min'),
    hip_cm: pick(hipW.lo, hipW.hi, 'max'),
    thigh_cm: pick(thighW.lo, thighW.hi, 'max'),
  };
}

/** Lateral (X-axis) extent at a given Y slice — for shoulder width.
 *  Returns cm; null if the slice is empty. */
export function extractWidthAtY(
  occupancy: Uint8Array,
  dims: { NX: number; NY: number; NZ: number },
  voxelSize: { x: number; y: number; z: number },
  yCm: number,
): number | null {
  const { NX, NY, NZ } = dims;
  const stepXcm = voxelSize.x * 100;
  const stepYcm = voxelSize.y * 100;
  const vy = Math.round(yCm / stepYcm);
  if (vy < 0 || vy >= NY) return null;
  let minX = NX, maxX = -1;
  for (let vz = 0; vz < NZ; vz++) {
    for (let vx = 0; vx < NX; vx++) {
      if (occupancy[vz * NX * NY + vy * NX + vx]) {
        if (vx < minX) minX = vx;
        if (vx > maxX) maxX = vx;
      }
    }
  }
  if (maxX < 0) return null;
  return (maxX - minX + 1) * stepXcm;
}

const SHOULDER_HEIGHT_RATIO = 0.85;
const HIP_HEIGHT_RATIO = 0.53;

/** Convenience that ties extraction + anatomical lookup together. */
export function extractFitMeasurements(
  occupancy: Uint8Array,
  dims: { NX: number; NY: number; NZ: number },
  voxelSize: { x: number; y: number; z: number },
  worldOrigin: { x: number; y: number; z: number },
  riderHeightM: number | null,
): FitMeasurements {
  const heightCm = riderHeightM != null ? riderHeightM * 100 : null;
  const series = extractCircumferences(occupancy, dims, voxelSize, worldOrigin);
  const anatomical = findAnatomicalMeasurements(series, heightCm);

  // Shoulder width: lateral extent at ~85% of height. Use the widest
  // slice in ±3 cm so the measurement lands on the actual shoulders
  // rather than landing in a neck dip.
  let shoulder_width_cm: number | null = null;
  if (heightCm) {
    const center = heightCm * SHOULDER_HEIGHT_RATIO;
    let best = 0;
    for (const dy of [-3, -1.5, 0, 1.5, 3]) {
      const w = extractWidthAtY(occupancy, dims, voxelSize, center + dy);
      if (w != null && w > best) best = w;
    }
    if (best > 0) shoulder_width_cm = best;
  }

  // Torso length: between shoulder ratio and hip ratio.
  const torso_length_cm =
    heightCm != null
      ? heightCm * (SHOULDER_HEIGHT_RATIO - HIP_HEIGHT_RATIO)
      : null;

  return {
    height_cm: heightCm,
    series,
    shoulder_width_cm,
    torso_length_cm,
    ...anatomical,
  };
}
